import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import analyticsService from '../../utils/analyticsService';

const GoalTracker = React.memo(() => {
  const { userProfile } = useAuth();
  const [goals, setGoals] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch real goals data from Supabase
  useEffect(() => {
    const fetchGoalsData = async () => {
      if (!userProfile?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const goalsData = await analyticsService.getUserGoals(userProfile.id);
        setGoals(goalsData);
      } catch (err) {
        console.error('Error fetching goals data:', err);
        setError('Failed to load goals data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchGoalsData();
  }, [userProfile?.id]);

  // Memoized utility functions to prevent recreation on every render
  const utils = useMemo(() => ({
    getProgressPercentage: (goal) => {
      return Math.min((goal.currentValue / goal.targetValue) * 100, 100);
    },

    getProgressColor: (percentage) => {
      if (percentage >= 90) return 'bg-green-500';
      if (percentage >= 70) return 'bg-blue-500';
      if (percentage >= 50) return 'bg-yellow-500';
      return 'bg-red-500';
    },

    getCategoryIcon: (category) => {
      switch (category) {
        case 'performance':
          return '📈';
        case 'productivity':
          return '⚡';
        case 'quality':
          return '⭐';
        case 'skills':
          return '🎓';
        default:
          return '🎯';
      }
    }
  }), []);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'performance':
        return 'bg-blue-100 text-blue-800';
      case 'productivity':
        return 'bg-green-100 text-green-800';
      case 'quality':
        return 'bg-yellow-100 text-yellow-800';
      case 'skills':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4">
        <h3 className="text-lg font-medium mb-2">Error Loading Goals</h3>
        <p>{error}</p>
        <button 
          className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 py-1 px-4 rounded transition-colors"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }
  
  if (goals.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Goal Tracker</h2>
            <p className="text-gray-600">Set and track your professional development goals</p>
          </div>
          {/* <Button onClick={() => setShowCreateModal(true)}>
            🎯 Set New Goal
          </Button> */}
         
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">🎯</div>
          <h3 className="text-lg font-medium mb-2">No Goals Set Yet</h3>
          <p className="mb-4">Start setting professional goals to track your progress and growth</p>
           <h3>Feature Coming Soon</h3>
          {/* <Button onClick={() => setShowCreateModal(true)}>
            Create Your First Goal
          </Button> */}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Goal Tracker</h2>
          <p className="text-gray-600">Set and track your professional development goals</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          🎯 Set New Goal
        </Button>
      </div>

      {/* Goals Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{goals.filter(g => g.status === 'active').length}</div>
          <div className="text-sm text-gray-600">Active Goals</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {goals.filter(g => utils.getProgressPercentage(g) >= 100).length}
          </div>
          <div className="text-sm text-gray-600">Completed</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {Math.round(goals.reduce((acc, goal) => acc + utils.getProgressPercentage(goal), 0) / goals.length)}%
          </div>
          <div className="text-sm text-gray-600">Avg Progress</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-purple-600">
            {goals.filter(g => getDaysRemaining(g.deadline) <= 7).length}
          </div>
          <div className="text-sm text-gray-600">Due This Week</div>
        </div>
      </div>

      {/* Goals List */}
      <div className="space-y-4">
        {goals.map(goal => {
          const progress = utils.getProgressPercentage(goal);
          const daysRemaining = getDaysRemaining(goal.deadline);
          
          return (
            <div key={goal.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              {/* Goal Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">{utils.getCategoryIcon(goal.category)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{goal.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(goal.category)}`}>
                        {goal.category.charAt(0).toUpperCase() + goal.category.slice(1)}
                      </span>
                      <span className="text-sm text-gray-500">
                        Due: {formatDate(goal.deadline)}
                      </span>
                      <span className={`text-sm font-medium ${
                        daysRemaining <= 7 ? 'text-red-600' : 
                        daysRemaining <= 14 ? 'text-yellow-600' : 'text-gray-600'
                      }`}>
                        {daysRemaining > 0 ? `${daysRemaining} days left` : 'Overdue'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    {goal.currentValue}{goal.unit}
                  </div>
                  <div className="text-sm text-gray-600">
                    of {goal.targetValue}{goal.unit}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progress</span>
                  <span className="text-sm font-medium text-gray-900">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${utils.getProgressColor(progress)} transition-all duration-300`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Milestones */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Milestones</h4>
                <div className="flex items-center space-x-2 overflow-x-auto">
                  {goal.milestones.map((milestone, index) => (
                    <div key={index} className="flex flex-col items-center min-w-0 flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        milestone.achieved 
                          ? 'bg-green-500 text-white' 
                          : goal.currentValue >= milestone.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {milestone.achieved ? '✓' : milestone.value}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 text-center">
                        {milestone.value}{goal.unit}
                      </div>
                      {milestone.achieved && milestone.date && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(milestone.date)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm">
                    📝 Update Progress
                  </Button>
                  <Button variant="outline" size="sm">
                    📊 View Details
                  </Button>
                </div>
                <Button variant="outline" size="sm">
                  ⚙️ Edit Goal
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Goal Modal Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Goal</h3>
            <p className="text-gray-600 mb-4">Goal creation form would be implemented here.</p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={() => setShowCreateModal(false)}>
                Create Goal
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default GoalTracker;

